import { Controller } from "@hotwired/stimulus"

// Draws a custom-report chart with ApexCharts. CustomReportChartRenderer#render_interactive
// emits the trusted, empty container we attach to:
//
//   <div class="cc-report-chart" data-controller="report-chart" data-report-chart-spec-value="…">
//
// The spec is library-neutral — { type, title, categories, series: [{name, data}], yMin, yMax } —
// so swapping charting libraries later is a change to this file alone; the server contract holds.
//
// Charts render entirely on the client; a browser print (Cmd+P) captures the live chart, so there
// is no server-side image. ApexCharts loads as a code-split dynamic import, so it is fetched only
// on pages that show a chart rather than weighing down the main bundle.

const PALETTE = [
  "#2563eb", "#16a34a", "#db2777", "#d97706",
  "#7c3aed", "#0891b2", "#dc2626", "#4b5563",
]

export default class extends Controller {
  static values = { spec: Object }

  async connect() {
    try {
      const { default: ApexCharts } = await import("apexcharts")
      // A turbo navigation (or re-run) can disconnect us while the dynamic import is in
      // flight; bail rather than render onto a detached element (ApexCharts would throw).
      if (!this.element.isConnected) return
      // Render into a dedicated child so ApexCharts owns a clean element of its own.
      this.mount = document.createElement("div")
      this.element.appendChild(this.mount)
      this.chart = new ApexCharts(this.mount, this.options())
      await this.chart.render()
    } catch (error) {
      console.error("report-chart: failed to render", error)
      this.element.textContent = "Unable to render chart."
    }
  }

  disconnect() {
    // Tear the chart and its mount fully down so a reconnect starts clean rather than
    // stacking a second canvas and orphaning the prior instance's resize listener.
    this.chart?.destroy()
    this.mount?.remove()
    this.chart = this.mount = undefined
  }

  // Translate the library-neutral spec into ApexCharts options. Optional keys are added only
  // when they apply: passing an explicit `key: undefined` (e.g. stroke) makes ApexCharts choke
  // on it and corrupt shared state, breaking every later chart on the page.
  options() {
    const spec = this.specValue
    const chart = {
      height: 320,
      fontFamily: "ui-sans-serif, system-ui, sans-serif",
      toolbar: { show: true },
    }
    const options = { colors: PALETTE, noData: { text: "No data" } }
    if (spec.title) options.title = { text: spec.title, align: "center" }

    // pie: a flat value array with matching labels — no axes; show slice-percentage labels.
    if (spec.type === "pie") {
      options.chart = { ...chart, type: "pie" }
      options.series = spec.series[0]?.data ?? []
      options.labels = spec.categories
      return options
    }

    // bar | line | area | stacked_bar — all cartesian; stacked_bar is a stacked bar chart.
    const stacked = spec.type === "stacked_bar"
    options.chart = { ...chart, type: stacked ? "bar" : spec.type, stacked }
    options.series = spec.series
    options.xaxis = { categories: spec.categories }
    options.dataLabels = { enabled: false }
    if (spec.yMin != null || spec.yMax != null) {
      options.yaxis = {}
      if (spec.yMin != null) options.yaxis.min = spec.yMin
      if (spec.yMax != null) options.yaxis.max = spec.yMax
    }
    if (spec.type === "line") options.stroke = { curve: "straight", width: 2 }
    return options
  }
}
